import { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { MessageSquareText, RefreshCw } from "lucide-react"

import { fetchImessageMetrics, type ImessageMetrics, type ImessagePerson } from "@/lib/api"
import { NameList } from "@/components/NameList"
import { NamedBar } from "@/components/NamedBar"
import { cn, nameOrPhone } from "@/lib/utils"

const DAY_RANGES = [7, 30, 90, 365]

function fmt(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-US", {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
}

function secs(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—"
  return n < 60 ? `${n.toFixed(1)}s` : `${Math.round(n / 60)}m`
}

function personName(p: ImessagePerson): string {
  return nameOrPhone(p.name) || p.user_id.slice(0, 8)
}

const STEP_COLORS: Record<string, string> = {
  texted_us: "#0ea5e9",
  we_replied: "#0ea5e9",
  kept_talking: "#10b981",
  ready_to_build: "#10b981",
  plan_built: "#8b5cf6",
  opened_app: "#8b5cf6",
}

export function ImessagePage() {
  const [days, setDays] = useState(30)
  const [data, setData] = useState<ImessageMetrics | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      setData(await fetchImessageMetrics(days))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setIsLoading(false)
    }
  }, [days])

  useEffect(() => { void load() }, [load])

  const people = data?.people ?? []
  const funnel = data?.funnel ?? []
  const scale = funnel[0]?.count ?? 0
  const health = data?.health
  const events = data?.posthog ?? null
  const waiting = people.filter(
    (p) => !p.has_plan && (p.still_needed.length > 0 || !p.we_replied),
  )

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <MessageSquareText className="size-6 text-muted-foreground" />
        <h1 className="text-2xl font-bold">iMessage</h1>
        <span className="text-sm text-muted-foreground">
          read-only · people who met us over text instead of in the app
        </span>
      </div>

      <div className="rounded-lg border bg-card p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          Window
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border px-2 py-1.5 text-sm text-foreground"
          >
            {DAY_RANGES.map((d) => (
              <option key={d} value={d}>last {d} days</option>
            ))}
          </select>
        </label>
        <button
          onClick={() => void load()}
          disabled={isLoading}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw className={cn("size-4", isLoading && "animate-spin")} />
          Refresh
        </button>
        <div className="ml-auto text-sm text-muted-foreground">
          {people.length} {people.length === 1 ? "person" : "people"}
          {health?.first_text_at && <> · first text {fmt(health.first_text_at)}</>}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {data && !people.length && !error && (
        <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
          Nobody has texted us in the last {days} days.
        </div>
      )}

      {!!people.length && (
        <>
          <section className="rounded-lg border bg-card p-4">
            <div className="mb-1 text-sm font-semibold">How far people get over text</div>
            <p className="text-sm text-muted-foreground mb-4">
              Counted from the database, not from analytics — every bar is a fact about
              a real account. Click a bar for the names.
            </p>
            <div className="space-y-3">
              {funnel.map((step) => (
                <NamedBar
                  key={step.key}
                  label={step.label}
                  value={step.count}
                  unknown={step.unknown}
                  total={scale}
                  color={STEP_COLORS[step.key] ?? "#0ea5e9"}
                  caption={
                    step.unknown
                      ? "unknown — we could not reach analytics"
                      : scale > 0 && step.key !== "texted_us"
                        ? `${Math.round((step.count / scale) * 100)}% of everyone who texted`
                        : undefined
                  }
                  sub={
                    step.unknown ? (
                      <>
                        Not a zero. This is the one step only analytics can answer, and
                        it did not answer — read the bar as blank, not as nobody.
                      </>
                    ) : step.key === "opened_app" ? (
                      <>The only step analytics answers rather than the database.</>
                    ) : step.team > 0 ? (
                      <>{step.team} of these {step.team === 1 ? "is" : "are"} us</>
                    ) : undefined
                  }
                  hover={
                    <>
                      <div className="text-base font-semibold mb-2">{step.label}</div>
                      <NameList title="Who" names={step.names} tone="good" />
                    </>
                  }
                />
              ))}
            </div>
          </section>

          {health && (
            <section className="rounded-lg border bg-card p-4">
              <div className="mb-3 text-sm font-semibold">Is the machine working</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <Stat label="Texts we handled" value={String(health.runs)} />
                <Stat
                  label="Answered"
                  value={`${health.replied_runs ?? 0}`}
                  hint={
                    health.runs > (health.replied_runs ?? 0)
                      ? `${health.runs - (health.replied_runs ?? 0)} produced no reply`
                      : "every one got a reply"
                  }
                />
                <Stat label="Typical reply time" value={secs(health.reply_p50_seconds)} />
                <Stat
                  label="Slowest 10%"
                  value={secs(health.reply_p90_seconds)}
                  hint="a person notices past ~60s"
                />
                <Stat
                  label="Test accounts hidden"
                  value={String(health.bots_excluded ?? 0)}
                  hint="teammates still count as people"
                />
              </div>
              {!!health.by_status && Object.keys(health.by_status).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  {Object.entries(health.by_status).map(([status, n]) => (
                    <span
                      key={status}
                      className={cn(
                        "rounded-md px-2 py-1 font-medium",
                        status === "done"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-red-100 text-red-800",
                      )}
                    >
                      {status} · {n}
                    </span>
                  ))}
                </div>
              )}
            </section>
          )}

          {!!waiting.length && (
            <section className="rounded-lg border bg-card p-4">
              <div className="mb-1 text-sm font-semibold">
                Stuck mid-conversation · {waiting.length}
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Nobody here has a plan yet. Either the coach is still missing
                something — its own words for what — or we never wrote back at all,
                which is the worse row and the one a "what's missing" list hides.
              </p>
              <div className="flex flex-wrap gap-2">
                {waiting.map((p) => (
                  <span
                    key={p.user_id}
                    className="rounded-md border bg-muted/40 px-2.5 py-1 text-xs"
                  >
                    <span className="font-medium">{personName(p)}</span>
                    <span className="text-muted-foreground">
                      {" — "}
                      {[
                        p.still_needed.length && `needs ${p.still_needed.join(" + ")}`,
                        !p.we_replied && "we never wrote back",
                      ]
                        .filter(Boolean)
                        .join(" \u00b7 ")}
                    </span>
                  </span>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-lg border bg-card p-4">
            <div className="mb-3 text-sm font-semibold">Everyone who texted us</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                  <tr className="border-b">
                    <th className="text-left py-2 pr-3 font-medium">Person</th>
                    <th className="text-right py-2 px-3 font-medium">Their texts</th>
                    <th className="text-left py-2 px-3 font-medium">First</th>
                    <th className="text-left py-2 px-3 font-medium">Last</th>
                    <th className="text-left py-2 px-3 font-medium">Still needs</th>
                    <th className="text-left py-2 px-3 font-medium">Plan</th>
                    <th className="text-left py-2 pl-3 font-medium">Opened app</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.user_id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 pr-3">
                        {p.has_profile ? (
                          <Link
                            to={`/users/${p.user_id}`}
                            className="font-medium hover:underline"
                          >
                            {personName(p)}
                          </Link>
                        ) : (
                          <span className="font-medium">{personName(p)}</span>
                        )}
                        {p.is_team && (
                          <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                            team
                          </span>
                        )}
                        {!p.we_replied && (
                          <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-800">
                            never answered
                          </span>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {p.onboarding_step || "no profile"}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">{p.their_texts}</td>
                      <td className="py-2 px-3 whitespace-nowrap text-muted-foreground">{fmt(p.first_text)}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{fmt(p.last_text)}</td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {p.still_needed.length ? p.still_needed.join(", ") : "nothing"}
                      </td>
                      <td className="py-2 px-3">{p.has_plan ? "yes" : "no"}</td>
                      <td className="py-2 pl-3">
                        {p.opened_app === null ? "unknown" : p.opened_app ? "yes" : "no"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4">
            <div className="mb-1 text-sm font-semibold">What the text coach reported</div>
            <p className="text-sm text-muted-foreground mb-3">
              Analytics, for these same people only. A zero on a wired event means nobody
              did it; the greyed rows are events that exist in code but nothing calls yet,
              so they can never report anything.
            </p>
            {data?.posthog_error && (
              <div className="rounded-md border border-amber-500/40 bg-amber-50 p-2 text-xs text-amber-900">
                Analytics didn't answer — the rest of this page is unaffected.
              </div>
            )}
            {events && (
              <table className="w-full text-sm max-w-xl">
                <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                  <tr className="border-b">
                    <th className="text-left py-2 pr-3 font-medium">Event</th>
                    <th className="text-right py-2 px-3 font-medium">Times</th>
                    <th className="text-right py-2 pl-3 font-medium">People</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr
                      key={e.event}
                      className={cn("border-b last:border-0", !e.wired && "opacity-50")}
                    >
                      <td className="py-1.5 pr-3 font-mono text-xs">
                        {e.event}
                        {!e.wired && (
                          <span className="ml-2 font-sans not-italic text-muted-foreground">
                            never wired up
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums">{e.count}</td>
                      <td className="py-1.5 pl-3 text-right tabular-nums">{e.people}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {data && (
        <p className="text-xs text-muted-foreground">
          Read at {fmt(data.generated_at)} · window opened {fmt(data.since)}
        </p>
      )}
    </div>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs font-medium">{label}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  )
}
